import { ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  createStyles,
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Theme,
  useMediaQuery,
  Box
} from '@material-ui/core'
import { TaxYear } from 'ustaxes/core/data'
import { YearsTaxesState } from 'ustaxes/redux'
import { drawerSectionsForYear } from './Menu'
import { getSectionCompletions } from './completionStatus'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    stepperContainer: {
      position: 'fixed' as const,
      top: 68,
      left: 0,
      right: 0,
      backgroundColor: theme.palette.background.paper,
      borderBottom:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(0,0,0,0.08)',
      padding: theme.spacing(0, 2),
      zIndex: theme.zIndex.appBar - 1
    },
    stepper: {
      backgroundColor: 'transparent',
      padding: theme.spacing(1.5, 0),
      maxWidth: 900,
      margin: '0 auto'
    },
    stepLabel: {
      '& .MuiStepLabel-label': {
        fontSize: '0.78rem',
        fontWeight: 600,
        marginTop: 4,
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.65rem'
        }
      },
      '& .MuiStepLabel-label.MuiStepLabel-active': {
        color: theme.palette.primary.main,
        fontWeight: 700
      },
      '& .MuiStepLabel-label.MuiStepLabel-completed': {
        color: '#2E7D32',
        fontWeight: 600
      }
    },
    stepButton: {
      borderRadius: 8,
      padding: theme.spacing(0.5),
      '&:hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(26, 35, 126, 0.04)'
      }
    }
  })
)

const StepProgress = (): ReactElement => {
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs')
  )

  const activeYear: TaxYear = useSelector(
    (state: YearsTaxesState) => state.activeYear
  )
  const info = useSelector(
    (state: YearsTaxesState) => state[state.activeYear]
  )

  const sections = drawerSectionsForYear(activeYear)
  const completions = getSectionCompletions(info)

  // Filter out the getting started section
  const displaySections = sections.filter(
    (s) => s.title !== 'Ameritax'
  )

  // Find current active section index
  const currentSectionIndex = displaySections.findIndex((section) =>
    section.items.some((item) => item.url === location.pathname)
  )

  const handleStepClick = (index: number) => {
    const section = displaySections[index] as { items: { url: string }[] } | undefined
    if (section != null && section.items.length > 0) {
      navigate(section.items[0].url)
    }
  }

  return (
    <Box className={classes.stepperContainer}>
      <Stepper
        activeStep={currentSectionIndex >= 0 ? currentSectionIndex : 0}
        alternativeLabel={!isMobile}
        nonLinear
        className={classes.stepper}
      >
        {displaySections.map((section, idx) => {
          const completion = completions.find(
            (c) => c.title === section.title
          )
          return (
            <Step
              key={section.title}
              completed={completion?.complete ?? false}
            >
              <StepButton
                onClick={() => handleStepClick(idx)}
                className={classes.stepButton}
              >
                <StepLabel className={classes.stepLabel}>
                  {isMobile
                    ? section.title.split(' ')[0]
                    : section.title}
                </StepLabel>
              </StepButton>
            </Step>
          )
        })}
      </Stepper>
    </Box>
  )
}

export default StepProgress
